import type {WixBookingService, WixImageInfo} from '@wix/devzai-common-wix';
import {RateType, WixBookingType} from '@wix/devzai-common-wix';
import type {BymoPageProps} from '../bymo-pages/bymo-page-props';
import {bymoPageFormatDateTime} from '../../components/bymo-page-locales-utils/bymo-page-locales-utils';
import type {CurrencyCode, Values} from '@wix/devzai-utils-common';
import {currencyFormatPrice, currencyGetAllCodes, evaluateFunction} from '@wix/devzai-utils-common';
import {ServiceType} from '@wix/ambassador-services-2/types';

export const DoppeDtoBookingServiceType = {
    APPOINTMENT: ServiceType.APPOINTMENT,
    Class: ServiceType.CLASS,
    COURSE: ServiceType.COURSE,
    Unknown: ServiceType.UNKNOWN_SERVICE_TYPE
} as const;

export type DoppeDtoBookingServiceType = Values<typeof DoppeDtoBookingServiceType>;

export interface DoppeDtoBookingService {
    id: string;
    name: string;
    description: string;
    /**
     * URL-friendly slug for the service.
     * Used for clean URLs (e.g., /service/consultation instead of /service/abc123).
     * Falls back to id if not available.
     */
    slug: string;
    media: WixImageInfo[] | null;
    locations: WixBookingService.Location[];
    payment?: WixBookingService.Payment;
    scheduale?: WixBookingService.Schedule;
    type: DoppeDtoBookingServiceType;
    bookingPageUrl?: string;
    calendarPageUrl?: string;
    servicePageUrl?: string;
    createdDate: Date | null;
    capacity?: number;
    /**
     * Whether the service is available online (video conferencing).
     * Determined by the conferencing.enabled field from the Wix Booking service.
     */
    isAvailableOnline: boolean;
    /**
     * Whether the service is hidden from the services list.
     * When true, the service is not visible to customers.
     * Source: Wix Booking API `hidden` field.
     */
    hidden?: boolean;
}

export namespace DoppeDtoBookingService {
    export type Id = DoppeDtoBookingService['id'];

    export type Type = DoppeDtoBookingServiceType;
}

export function doppeDtoBookingServiceConvertToFormattedPrice<EmptyValue>(payment: WixBookingService.Payment | undefined, emptyValue: EmptyValue): string | EmptyValue {

    if (!payment) {
        return emptyValue;
    }

    try {
        switch (payment.rateType) {
            case RateType.FIXED: {
                const currency = payment.fixed?.price?.currency as CurrencyCode;
                const price = payment.fixed?.price?.value ? parseFloat(payment.fixed?.price?.value) : NaN;
                const hasCurrency = (currency !== undefined && currencyGetAllCodes().includes(currency))

                return !isNaN(price) && hasCurrency ? currencyFormatPrice({
                    code: currency,
                    price: price
                }) : (payment.fixed?.price.formattedValue ?? emptyValue)
            }
            case RateType.VARIED: {
                if (!payment.varied?.minPrice?.value || !payment.varied?.maxPrice?.value) {
                    const defaultPrice = payment.varied?.defaultPrice;
                    const currency = defaultPrice?.currency as CurrencyCode;
                    const price = defaultPrice?.value ? parseFloat(defaultPrice?.value) : NaN;
                    const hasCurrency = (currency !== undefined && currencyGetAllCodes().includes(currency))

                    return !isNaN(price) && hasCurrency ? currencyFormatPrice({
                        code: currency,
                        price: price
                    }) : (defaultPrice?.formattedValue ?? emptyValue);
                } else if (payment.varied?.minPrice.value !== payment.varied?.maxPrice?.value) {
                    const formattedMinPrice = evaluateFunction(() => {
                        const minPrice = payment.varied?.minPrice;
                        const currency = minPrice?.currency as CurrencyCode;
                        const price = minPrice?.value ? parseFloat(minPrice?.value) : NaN;
                        const hasCurrency = (currency !== undefined && currencyGetAllCodes().includes(currency))

                        return !isNaN(price) && hasCurrency ? currencyFormatPrice({
                            code: currency,
                            price: price
                        }) : (minPrice?.formattedValue ?? emptyValue);
                    })

                    const formattedMaxPrice = evaluateFunction(() => {
                        const maxPrice = payment.varied?.maxPrice;
                        const currency = maxPrice?.currency as CurrencyCode;
                        const price = maxPrice?.value ? parseFloat(maxPrice?.value) : NaN;
                        const hasCurrency = (currency !== undefined && currencyGetAllCodes().includes(currency))

                        return !isNaN(price) && hasCurrency ? currencyFormatPrice({
                            code: currency,
                            price: price
                        }) : (maxPrice?.formattedValue ?? emptyValue);
                    })

                    return `${formattedMinPrice} - ${formattedMaxPrice}`;
                }


                const defaultPrice = payment.varied?.defaultPrice;
                const currency = defaultPrice?.currency as CurrencyCode;
                const price = defaultPrice?.value ? parseFloat(defaultPrice?.value) : NaN;
                const hasCurrency = (currency !== undefined && currencyGetAllCodes().includes(currency))

                return !isNaN(price) && hasCurrency ? currencyFormatPrice({
                    code: currency,
                    price: price
                }) : (defaultPrice?.formattedValue ?? emptyValue);
            }
            case RateType.CUSTOM:
                return payment.custom ?? '';
            case RateType.NO_FEE:
                return 'Free';
            case RateType.Unspecified:
                return '---';
        }
    }
    catch (e) {
        return emptyValue;
    }
}

/**
 * Returns pricing info with a flag indicating if the price should be prefixed with "From".
 * This is useful for varied pricing where there's a min/max range or just a starting price.
 */
export function doppeDtoBookingServiceGetPriceInfo<EmptyValue>(
    payment: WixBookingService.Payment | undefined,
    emptyValue: EmptyValue
): { price: string | EmptyValue; isFromPrice: boolean } {

    if (!payment) {
        return { price: emptyValue, isFromPrice: false };
    }

    try {
        switch (payment.rateType) {
            case RateType.FIXED: {
                const currency = payment.fixed?.price?.currency as CurrencyCode;
                const price = payment.fixed?.price?.value ? parseFloat(payment.fixed?.price?.value) : NaN;
                const hasCurrency = (currency !== undefined && currencyGetAllCodes().includes(currency))

                const formattedPrice = !isNaN(price) && hasCurrency ? currencyFormatPrice({
                    code: currency,
                    price: price
                }) : (payment.fixed?.price.formattedValue ?? emptyValue);

                return { price: formattedPrice, isFromPrice: false };
            }
            case RateType.VARIED: {
                // If there's a min price that differs from max, show "From {minPrice}"
                if (payment.varied?.minPrice?.value && payment.varied?.maxPrice?.value &&
                    payment.varied.minPrice.value !== payment.varied.maxPrice.value) {
                    const minPrice = payment.varied.minPrice;
                    const currency = minPrice?.currency as CurrencyCode;
                    const price = minPrice?.value ? parseFloat(minPrice?.value) : NaN;
                    const hasCurrency = (currency !== undefined && currencyGetAllCodes().includes(currency))

                    const formattedPrice = !isNaN(price) && hasCurrency ? currencyFormatPrice({
                        code: currency,
                        price: price
                    }) : (minPrice?.formattedValue ?? emptyValue);

                    return { price: formattedPrice, isFromPrice: true };
                }

                // Fallback to default price without "From" prefix
                const defaultPrice = payment.varied?.defaultPrice;
                const currency = defaultPrice?.currency as CurrencyCode;
                const price = defaultPrice?.value ? parseFloat(defaultPrice?.value) : NaN;
                const hasCurrency = (currency !== undefined && currencyGetAllCodes().includes(currency))

                const formattedPrice = !isNaN(price) && hasCurrency ? currencyFormatPrice({
                    code: currency,
                    price: price
                }) : (defaultPrice?.formattedValue ?? emptyValue);

                return { price: formattedPrice, isFromPrice: false };
            }
            case RateType.CUSTOM:
                return { price: payment.custom ?? '', isFromPrice: false };
            case RateType.NO_FEE:
                return { price: 'Free', isFromPrice: false };
            case RateType.Unspecified:
                return { price: '---', isFromPrice: false };
        }
    }
    catch (e) {
        return { price: emptyValue, isFromPrice: false };
    }
}

export function doppeDtoBookingServiceLocationConvertToActionFormattedLocation(location: WixBookingService.Location): string {
    return location?.formattedAddress ?? '';
}

export function doppeDtoBookingServiceCapacityConvertToActionFormattedCapacity(capacity: number): string {
    return capacity > 1 ? `${capacity} Participants` : `${capacity} Participant`;
}

export function doppeDtoBookingServiceScheduleConvertToActionFormattedDuration(options: {
    schedule: WixBookingService.Schedule,
    serviceType: WixBookingService.ServiceType
}): string[] | null {
    if (options.serviceType === WixBookingType.APPOINTMENT) {
        if (options.schedule.availabilityConstraints?.sessionDurations?.length) {
            return options.schedule.availabilityConstraints.sessionDurations.map(duration => `${duration} minutes`);
        }
    }
    return null;
}

export function doppeDtoBookingServiceScheduleConvertToActionFormattedSchedule(options: {
    schedule: WixBookingService.Schedule,
    serviceType: WixBookingService.ServiceType,
    bymoPageProps?: BymoPageProps;
}): string | null {
    if (options.serviceType === WixBookingType.APPOINTMENT) {
        if (options.schedule.firstSessionStart) {
            return `Starts ${bymoPageFormatDateTime(
                options.schedule.firstSessionStart,
                {
                    month: 'short',
                    day: '2-digit',
                },
                options.bymoPageProps,
            )}`;
        }
    }
    return null;
}

/**
 * Resolves the correct booking page path based on service type.
 * For courses, returns undefined — courses use the booking form page with query parameters
 * (see {@link doppeHtmlSiteResolveBookingFormUrl}).
 * Other service types use the calendar page (calendarPageUrl) for date/time selection.
 */
export function doppeDtoBookingServiceResolveBookingPath(service: DoppeDtoBookingService): string | undefined {
    if (service.type === DoppeDtoBookingServiceType.COURSE) {
        return undefined;
    }

    return service.calendarPageUrl ?? service.bookingPageUrl;
}

/**
 * Options for creating a dummy booking service with localized content and images.
 */
export interface DoppeDtoBookingServiceCreateDummyOptions {
    /**
     * Localized service name.
     */
    name: string;
    /**
     * Localized service description.
     */
    description: string;
    /**
     * Service image (WixImageInfo format).
     */
    image: WixImageInfo | null;
}

/**
 * Creates a dummy booking service with placeholder content.
 * Used when no actual service is available (e.g., for preview or empty state).
 */
export function doppeDtoBookingServiceCreateDummy(
    options: DoppeDtoBookingServiceCreateDummyOptions
): DoppeDtoBookingService {
    return {
        id: 'dummy-service',
        name: options.name,
        description: options.description,
        slug: 'dummy-service',
        media: options.image ? [options.image] : null,
        locations: [],
        type: DoppeDtoBookingServiceType.APPOINTMENT,
        createdDate: null,
        isAvailableOnline: false
    };
}